import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectItem} from 'helpers/hooks/utils';
import StyledWrapper from 'components/templates/Wrappers/Basic/Wrapper/Wrapper.styles';
import utils from 'helpers/utils';
import {useDialog} from 'components/organisms/Dialogs/Dialog/Dialog';
import {withMemo} from 'helpers/wrapper';

export const WrapperContext = React.createContext(null)

export function useWrapper () {
  return React.useContext(WrapperContext);
}

const Wrapper = withMemo(React.forwardRef((props, ref) => {
  const {
    wrapper,
    reset,
    inset,
    scroll,
    ...innerProps
  } = useComponentProps(props, 'Wrapper');

  // SCROLL: bounds are part of the content and scroll with the content so no extra margins needed, but margins are propagated
  // !SCROLL: fixed headers and footers you need margin to render and correctly stick in the right place
  // INSET: bounds are part of the page and scroll away, inset part has no extra margins and is not propagated.
  // scroll margins are always applied to enable scrolling to the right spot
  // scrollBounds --- are used by floating objects like table to make sure it's scroll within the right box
  // originalScrollBounds --- are used by virtualizer objects like table to make sure of it's startOffset

  const dialog = useDialog();

  const toPixels = (rem) => {
    if (rem && rem.toString().endsWith('rem')) {
      return utils.rem2Pixel(rem, innerProps.theme.typography.htmlFontSize);
    } else {
      return rem ?? 0;
    }
  }

  const parentWrapper = useWrapper();
  const boundsPixels = {
    top: toPixels(wrapper?.bounds?.top),
    left: toPixels(wrapper?.bounds?.left),
    right: toPixels(wrapper?.bounds?.right),
    bottom: toPixels(wrapper?.bounds?.bottom)
  };
  const boundsRem = {
    top: innerProps.theme.layout(boundsPixels.top),
    left: innerProps.theme.layout(boundsPixels.left),
    right: innerProps.theme.layout(boundsPixels.right),
    bottom: innerProps.theme.layout(boundsPixels.bottom)
  };

  const boundsCumPixels = !inset ? {...boundsPixels} : {
    top: (inset?.includes('top') ? 0 : boundsPixels.top),
    left: (inset?.includes('left') ? 0 : boundsPixels.left),
    bottom: (inset?.includes('bottom') ? 0 : boundsPixels.bottom),
    right: (inset?.includes('right') ? 0 : boundsPixels.right)
  };
  boundsCumPixels.top += toPixels(parentWrapper?.boundsCum?.top);
  boundsCumPixels.left += toPixels(parentWrapper?.boundsCum?.left);
  boundsCumPixels.bottom += toPixels(parentWrapper?.boundsCum?.bottom);
  boundsCumPixels.right += toPixels(parentWrapper?.boundsCum?.right);

  const boundsCumRem = {
    top: innerProps.theme.layout(boundsCumPixels.top),
    left: innerProps.theme.layout(boundsCumPixels.left),
    right: innerProps.theme.layout(boundsCumPixels.right),
    bottom: innerProps.theme.layout(boundsCumPixels.bottom)
  };

  const boundsOriginalCumPixels = {...boundsPixels};
  boundsOriginalCumPixels.top += toPixels(parentWrapper?.boundsOriginalCum?.top);
  boundsOriginalCumPixels.left += toPixels(parentWrapper?.boundsOriginalCum?.left);
  boundsOriginalCumPixels.bottom += toPixels(parentWrapper?.boundsOriginalCum?.bottom);
  boundsOriginalCumPixels.right += toPixels(parentWrapper?.boundsOriginalCum?.right);

  const boundsOriginalCumRem = {
    top: innerProps.theme.layout(boundsOriginalCumPixels.top),
    left: innerProps.theme.layout(boundsOriginalCumPixels.left),
    right: innerProps.theme.layout(boundsOriginalCumPixels.right),
    bottom: innerProps.theme.layout(boundsOriginalCumPixels.bottom)
  };

  const scrollBoundsPixels = {
    top: toPixels(wrapper?.scrollBounds?.top),
    left: toPixels(wrapper?.scrollBounds?.left),
    right: toPixels(wrapper?.scrollBounds?.right),
    bottom: toPixels(wrapper?.scrollBounds?.bottom)
  };
  const scrollBoundsRem = {
    top: innerProps.theme.layout(scrollBoundsPixels.top),
    left: innerProps.theme.layout(scrollBoundsPixels.left),
    right: innerProps.theme.layout(scrollBoundsPixels.right),
    bottom: innerProps.theme.layout(scrollBoundsPixels.bottom)
  };

  const scrollBoundsCumPixels = !inset ? {...scrollBoundsPixels} : {
    top: (inset?.includes('top') ? 0 : scrollBoundsPixels.top),
    left: (inset?.includes('left') ? 0 : scrollBoundsPixels.left),
    bottom: (inset?.includes('bottom') ? 0 : scrollBoundsPixels.bottom),
    right: (inset?.includes('right') ? 0 : scrollBoundsPixels.right)
  };
  scrollBoundsCumPixels.top += toPixels(parentWrapper?.scrollBoundsCum?.top);
  scrollBoundsCumPixels.left += toPixels(parentWrapper?.scrollBoundsCum?.left);
  scrollBoundsCumPixels.bottom += toPixels(parentWrapper?.scrollBoundsCum?.bottom);
  scrollBoundsCumPixels.right += toPixels(parentWrapper?.scrollBoundsCum?.right);

  const scrollBoundsCumRem = {
    top: innerProps.theme.layout(scrollBoundsCumPixels.top),
    left: innerProps.theme.layout(scrollBoundsCumPixels.left),
    right: innerProps.theme.layout(scrollBoundsCumPixels.right),
    bottom: innerProps.theme.layout(scrollBoundsCumPixels.bottom)
  };

  const scrollBoundsOriginalCumPixels = {...scrollBoundsPixels};
  scrollBoundsOriginalCumPixels.top += toPixels(parentWrapper?.scrollBoundsOriginalCum?.top);
  scrollBoundsOriginalCumPixels.left += toPixels(parentWrapper?.scrollBoundsOriginalCum?.left);
  scrollBoundsOriginalCumPixels.bottom += toPixels(parentWrapper?.scrollBoundsOriginalCum?.bottom);
  scrollBoundsOriginalCumPixels.right += toPixels(parentWrapper?.scrollBoundsOriginalCum?.right);

  const scrollBoundsOriginalCumRem = {
    top: innerProps.theme.layout(scrollBoundsOriginalCumPixels.top),
    left: innerProps.theme.layout(scrollBoundsOriginalCumPixels.left),
    right: innerProps.theme.layout(scrollBoundsOriginalCumPixels.right),
    bottom: innerProps.theme.layout(scrollBoundsOriginalCumPixels.bottom)
  };

  const boundsMemo = useEffectItem(boundsRem);
  const boundsCumMemo = useEffectItem(boundsCumRem);
  const boundsOriginalCumMemo = useEffectItem(boundsOriginalCumRem);
  const scrollBoundsMemo = useEffectItem(scrollBoundsRem);
  const scrollBoundsCumMemo = useEffectItem(scrollBoundsCumRem);
  const scrollBoundsOriginalCumMemo = useEffectItem(scrollBoundsOriginalCumRem);
  const context = useMemo(() => ({
    bounds: boundsMemo,
    boundsCum: reset ? null : boundsCumMemo,
    boundsOriginalCum: reset ? null : boundsOriginalCumMemo,
    scrollBounds: scrollBoundsMemo,
    scrollBoundsCum: reset ? null : scrollBoundsCumMemo,
    scrollBoundsOriginalCum: reset ? null : scrollBoundsOriginalCumMemo,
    parentWrapper
  }), [reset, parentWrapper, boundsMemo, boundsCumMemo, boundsOriginalCumMemo,
    scrollBoundsMemo, scrollBoundsCumMemo, scrollBoundsOriginalCumMemo]);

  const isDialog = Boolean(dialog);
  const style = useMemo(() => ({
    marginTop: (reset || scroll || inset?.includes('top')) ? 0 : boundsRem.top,
    marginLeft: (reset || scroll || inset?.includes('left')) ? 0 : boundsRem.left,
    marginRight: (reset || scroll || inset?.includes('right')) ? 0 : boundsRem.right,
    marginBottom: (reset || scroll || inset?.includes('bottom')) ? 0 : boundsRem.bottom,

    scrollMarginTop: (reset) ? 0 : scrollBoundsCumRem.top,
    scrollMarginLeft: (reset) ? 0 : scrollBoundsCumRem.left,
    scrollMarginRight: (reset) ? 0 : scrollBoundsCumRem.right,
    scrollMarginBottom: (reset) ? 0 : scrollBoundsCumRem.bottom,
  }), [reset, scroll, inset, boundsRem.top, boundsRem.left, boundsRem.right, boundsRem.bottom,
    scrollBoundsCumRem.top, scrollBoundsCumRem.left, scrollBoundsCumRem.right, scrollBoundsCumRem.bottom]);

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <WrapperContext.Provider value={context}>
    <StyledWrapper ref={ref} {...innerProps} style={style} $dialog={isDialog}>
      {(wrapper || reset) ? innerProps.children : null}
    </StyledWrapper>
  </WrapperContext.Provider>
}));

Wrapper.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  wrapper: PropTypes.any,
  reset: PropTypes.bool,
  inset: PropTypes.string,
  scroll: PropTypes.bool
};

export default Wrapper;
